/**
 * Arena map generation + the mandatory preprocessing pass (spec §11 "Study the map").
 *
 * A 120×120 enclosed basalt arena: lava rivers (impassable, crossed only by narrow
 * bridges), giant mushrooms (cover/obstacle), shroom clouds (slow + hide), lava
 * geysers, and a central safe space. Everything is deterministic from a seed so the
 * sim harness can reproduce a match and the server can re-derive the same map.
 *
 * After generation we precompute, once, what the bots read instead of pathing from
 * scratch every tick: a movement-cost grid, a Dijkstra distance-to-safe-space map
 * from every tile, and the chokepoints (bridges + 1-wide passages). The player sees
 * the same via the minimap — the rules are readable, not hidden.
 */

import { mulberry32 } from '../map/prng';

export const enum Tile {
  Ground = 0,
  Lava = 1,     // impassable, 15 dmg/s
  Bridge = 2,   // the only lava crossing; a chokepoint
  Mushroom = 3, // obstacle + cover, impassable
  Shroom = 4,    // passable, −40% slow, hides you from bots (path weight 2.0)
  Safe = 5,     // central safe space: no damage, banks gold — NO healing (owner 2026-09-02)
  Wall = 6,     // enclosing border, impassable
}

export const ARENA_SIZE = 120;
export const SAFE_RADIUS = 6;

// A* / Dijkstra movement weights (spec: lava = ∞, shrooms = 2.0, bridge = 1.5).
export const IMPASSABLE = Infinity;
export function tileCost(t: Tile): number {
  switch (t) {
    case Tile.Lava: case Tile.Mushroom: case Tile.Wall: return IMPASSABLE;
    case Tile.Shroom: return 2.0;
    case Tile.Bridge: return 1.5;
    default: return 1;   // ground, safe
  }
}
export const passable = (t: Tile) => Number.isFinite(tileCost(t));

export interface Geyser { tx: number; ty: number; }

export interface ArenaMap {
  w: number;
  h: number;
  tiles: Uint8Array;             // Tile per cell, row-major
  safe: { cx: number; cy: number; r: number };
  geysers: Geyser[];
  // Preprocessing (spec "stored per match"):
  distToSafe: Float64Array;      // Dijkstra cost to the safe space; Infinity if unreachable
  chokepoints: { tx: number; ty: number }[];
}

const idx = (x: number, y: number, w: number) => y * w + x;

/** Generate a deterministic arena from a seed. */
export function generateArena(seed: number, size = ARENA_SIZE): ArenaMap {
  const rng = mulberry32(seed);
  const w = size, h = size;
  const tiles = new Uint8Array(w * h).fill(Tile.Ground);

  // Enclosing wall.
  for (let x = 0; x < w; x++) { tiles[idx(x, 0, w)] = Tile.Wall; tiles[idx(x, h - 1, w)] = Tile.Wall; }
  for (let y = 0; y < h; y++) { tiles[idx(0, y, w)] = Tile.Wall; tiles[idx(w - 1, y, w)] = Tile.Wall; }

  const cx = Math.floor(w / 2), cy = Math.floor(h / 2);
  const inSafe = (x: number, y: number) => Math.hypot(x - cx, y - cy) <= SAFE_RADIUS + 1;

  // 3–4 lava rivers: wandering horizontal/vertical bands, each with periodic bridges.
  // Rivers never cross the safe space (it must stay reachable and damage-free).
  const rivers = 3 + Math.floor(rng() * 2);
  for (let r = 0; r < rivers; r++) {
    const horizontal = rng() < 0.5;
    const span = horizontal ? w : h;
    let drift = Math.floor((0.2 + rng() * 0.6) * (horizontal ? h : w));
    const thickness = 2 + Math.floor(rng() * 2);
    const bridgeEvery = 14 + Math.floor(rng() * 10);
    for (let a = 1; a < span - 1; a++) {
      drift += Math.floor(rng() * 3) - 1;                       // random walk
      drift = Math.max(4, Math.min((horizontal ? h : w) - 5, drift));
      const isBridge = a % bridgeEvery === Math.floor(bridgeEvery / 2);
      for (let t = 0; t < thickness; t++) {
        const x = horizontal ? a : drift + t;
        const y = horizontal ? drift + t : a;
        if (x <= 0 || y <= 0 || x >= w - 1 || y >= h - 1) continue;
        if (inSafe(x, y)) continue;                             // keep the safe space clear
        // A bridge spans the FULL river thickness — a single lava tile left capping
        // it would make the crossing impassable and cut the map in two.
        tiles[idx(x, y, w)] = isBridge ? Tile.Bridge : Tile.Lava;
      }
    }
  }

  // Giant mushroom clusters (cover/obstacle) and shroom clouds, scattered off the
  // safe space and off lava (a mushroom in lava is pointless cover).
  const stamp = (kind: Tile, clusters: number, radius: number) => {
    for (let c = 0; c < clusters; c++) {
      const bx = 2 + Math.floor(rng() * (w - 4));
      const by = 2 + Math.floor(rng() * (h - 4));
      if (inSafe(bx, by)) continue;
      const rad = 1 + Math.floor(rng() * radius);
      for (let dy = -rad; dy <= rad; dy++) for (let dx = -rad; dx <= rad; dx++) {
        const x = bx + dx, y = by + dy;
        if (x <= 0 || y <= 0 || x >= w - 1 || y >= h - 1) continue;
        if (inSafe(x, y)) continue;
        if (Math.hypot(dx, dy) > rad) continue;
        const cell = idx(x, y, w);
        if (tiles[cell] === Tile.Lava || tiles[cell] === Tile.Bridge) continue;  // don't bury crossings
        tiles[cell] = kind;
      }
    }
  };
  stamp(Tile.Mushroom, 60, 3);
  stamp(Tile.Shroom, 24, 2);

  // Lava geysers: erupt on ground tiles away from the safe space.
  const geysers: Geyser[] = [];
  for (let g = 0; g < 18; g++) {
    const gx = 2 + Math.floor(rng() * (w - 4));
    const gy = 2 + Math.floor(rng() * (h - 4));
    if (inSafe(gx, gy) || tiles[idx(gx, gy, w)] !== Tile.Ground) continue;
    geysers.push({ tx: gx, ty: gy });
  }

  // Stamp the central safe circle last, so nothing overwrites it.
  for (let y = cy - SAFE_RADIUS; y <= cy + SAFE_RADIUS; y++)
    for (let x = cx - SAFE_RADIUS; x <= cx + SAFE_RADIUS; x++)
      if (Math.hypot(x - cx, y - cy) <= SAFE_RADIUS) tiles[idx(x, y, w)] = Tile.Safe;

  const map: ArenaMap = {
    w, h, tiles, safe: { cx, cy, r: SAFE_RADIUS }, geysers,
    distToSafe: new Float64Array(0), chokepoints: [],
  };
  map.distToSafe = computeDistToSafe(map);
  map.chokepoints = findChokepoints(map);
  return map;
}

/**
 * Multi-source Dijkstra: cost from every tile to the nearest safe-space cell, over
 * passable tiles with the movement weights. Bots retreat by descending this field
 * instead of A*-ing to the centre every tick.
 */
export function computeDistToSafe(map: ArenaMap): Float64Array {
  const { w, h, tiles } = map;
  const dist = new Float64Array(w * h).fill(Infinity);
  // Simple binary-heap Dijkstra.
  const heap: { c: number; i: number }[] = [];
  const push = (c: number, i: number) => {
    heap.push({ c, i });
    let n = heap.length - 1;
    while (n > 0) { const p = (n - 1) >> 1; if (heap[p].c <= heap[n].c) break; [heap[p], heap[n]] = [heap[n], heap[p]]; n = p; }
  };
  const pop = () => {
    const top = heap[0], last = heap.pop()!;
    if (heap.length) { heap[0] = last; let n = 0; for (;;) { const l = 2 * n + 1, r = l + 1; let s = n; if (l < heap.length && heap[l].c < heap[s].c) s = l; if (r < heap.length && heap[r].c < heap[s].c) s = r; if (s === n) break; [heap[s], heap[n]] = [heap[n], heap[s]]; n = s; } }
    return top;
  };
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (tiles[idx(x, y, w)] === Tile.Safe) { const i = idx(x, y, w); dist[i] = 0; push(0, i); }
  }
  const N4 = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  while (heap.length) {
    const { c, i } = pop();
    if (c > dist[i]) continue;
    const x = i % w, y = (i - x) / w;
    for (const [dx, dy] of N4) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const ni = idx(nx, ny, w);
      const step = tileCost(tiles[ni] as Tile);
      if (!Number.isFinite(step)) continue;              // impassable
      const nc = c + step;
      if (nc < dist[ni]) { dist[ni] = nc; push(nc, ni); }
    }
  }
  return dist;
}

/**
 * Chokepoints: every bridge tile, plus 1-wide passages — a passable tile with
 * passable neighbours on exactly one axis and impassable on the other (a gap the
 * bots funnel through). These are where a surrounded bot makes its stand.
 */
export function findChokepoints(map: ArenaMap): { tx: number; ty: number }[] {
  const { w, h, tiles } = map;
  const out: { tx: number; ty: number }[] = [];
  const pass = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && passable(tiles[idx(x, y, w)] as Tile);
  for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
    const t = tiles[idx(x, y, w)] as Tile;
    if (!passable(t)) continue;
    if (t === Tile.Bridge) { out.push({ tx: x, ty: y }); continue; }
    const horizOpen = pass(x - 1, y) && pass(x + 1, y);
    const vertOpen = pass(x, y - 1) && pass(x, y + 1);
    // 1-wide: open along exactly one axis, walled on the other.
    if (horizOpen && !vertOpen && !pass(x, y - 1) && !pass(x, y + 1)) out.push({ tx: x, ty: y });
    else if (vertOpen && !horizOpen && !pass(x - 1, y) && !pass(x + 1, y)) out.push({ tx: x, ty: y });
  }
  return out;
}

export const tileAt = (map: ArenaMap, x: number, y: number): Tile =>
  (x < 0 || y < 0 || x >= map.w || y >= map.h) ? Tile.Wall : map.tiles[idx(x, y, map.w)] as Tile;

export const inSafeSpace = (map: ArenaMap, x: number, y: number) =>
  Math.hypot(x - map.safe.cx, y - map.safe.cy) <= map.safe.r;
